import {
  CHANNEL_NAMES,
  CHARGE_TYPE_LABELS,
  PROFIT_ORDER_LIMIT,
  type ProductCostView,
  type ProfitAccountRow,
  type ProfitOrderDetail,
  type ProfitOrderRow,
  type ProfitProductRow,
  type ProfitReport,
} from '@wms/domain';
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, errorMessage } from '../../api';
import { downloadCsv } from '../../csv';
import { fmt } from '../../format';
import { PERIOD_PRESETS } from '../../performance';
import {
  CARGO_SOURCE_TEXT,
  STATE_TONE,
  TABLE_CIRO,
  accountChip,
  barBox,
  below,
  cargoUnknownText,
  channelChoices,
  costRows,
  estimatedText,
  kindText,
  marginText,
  noRecordsText,
  ordersCsv,
  pendingText,
  stateLabel,
  syncAsked,
  syncText,
  waterfall,
} from '../../profit';
import { useSession } from '../../session';
import { Empty, ErrorBanner, Spinner, useLoad, useToast } from '../../ui';
import { Icon } from '../../ui/icons';
import { useSort } from '../../ui/sort';
import { CostField } from './CostField';

type Tab = 'orders' | 'products' | 'deductions' | 'costs';

/** "−₺12,50" in red where an amount is a loss */
const Amount = ({ value, strong }: { value: string; strong?: boolean }) => (
  <span className={`nowrap${below(value) ? ' profit-neg' : ''}`} style={strong ? { fontWeight: 600 } : undefined}>
    {fmt.money(value)}
  </span>
);

/**
 * Kârlılık (#/karlilik): what the seller really earns. The ciro less every marketplace deduction (the
 * marketplaces' own finance records, estimates until they come), Pronto Depo's fees and the goods' cost the
 * seller enters, net of KDV, per order and per product; the cash the marketplace pays beside it. The seller's
 * alone: the warehouse never reads it.
 */
export function SellerProfit() {
  const session = useSession();
  const toast = useToast();
  const canEdit = session.me.role !== 'viewer';
  const [preset, setPreset] = useState('30');
  // days in the zone times are shown in (Ayarlar → Saat dilimi)
  const [range, setRange] = useState(() => PERIOD_PRESETS[1]!.range(fmt.today()));
  const [channel, setChannel] = useState('');
  const [tab, setTab] = useState<Tab>('orders');
  // "Maliyetleri gir": the tabs sit below the tiles and the waterfall, out of sight on a phone or a laptop
  const costsTab = useRef<HTMLButtonElement>(null);
  const toCosts = () => {
    setTab('costs');
    requestAnimationFrame(() => {
      costsTab.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      costsTab.current?.focus({ preventScroll: true });
    });
  };
  const [revision, setRevision] = useState(0);
  const [seen, setSeen] = useState<readonly string[]>([]);
  const [asking, setAsking] = useState(false);
  // a "Şimdi güncelle" under way: the page looks again every 20 seconds until the read ends
  const [syncing, setSyncing] = useState(false);
  const params = new URLSearchParams({ from: range.from, to: range.to });
  if (channel) params.set('channel', channel);
  const query = params.toString();
  const report = useLoad(() => api<ProfitReport>('GET', `/api/profit/report?${query}`), [query, revision, syncing], syncing ? 20_000 : 300_000);
  const r = report.data;
  // the chips keep every marketplace once seen, whichever one is picked
  useEffect(() => {
    if (r) {
      setSeen((before) => channelChoices(r, before));
      setSyncing(r.sync.some((s) => syncAsked(s)));
    }
  }, [r]);

  const askSync = async () => {
    setAsking(true);
    try {
      const res = await api<{ requested: string[]; waiting: { credentialId: string; nextAt: string; running: boolean }[] }>('POST', '/api/profit/sync', {});
      if (res.requested.length > 0) toast('Finans kayıtları okunuyor; birkaç dakika içinde sayfa güncellenir', 'ok');
      else if (res.waiting.some((w) => w.running)) toast('Finans kayıtları şu anda okunuyor; bitince sayfa kendiliğinden güncellenir', 'info');
      else if (res.waiting.length > 0) toast(`Az önce istendi; ${fmt.dateTime(res.waiting[0]!.nextAt)} sonrası yeniden isteyebilirsiniz`, 'info');
      else toast('Finans kaydı okunabilen pazaryeri hesabınız yok', 'info');
      void report.reload();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setAsking(false);
    }
  };

  return (
    <>
      <div className="row">
        {PERIOD_PRESETS.map((p) => (
          <button
            key={p.key}
            className={`chip select${preset === p.key ? ' on' : ''}`}
            onClick={() => {
              setPreset(p.key);
              setRange(p.range(fmt.today()));
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="row">
        <input className="input" type="date" style={{ maxWidth: 160 }} value={range.from} max={range.to} onChange={(e) => (setPreset(''), setRange({ ...range, from: e.target.value }))} aria-label="Başlangıç" />
        <input className="input" type="date" style={{ maxWidth: 160 }} value={range.to} min={range.from} onChange={(e) => (setPreset(''), setRange({ ...range, to: e.target.value }))} aria-label="Bitiş" />
        {seen.length > 1 && (
          <>
            <button className={`chip select${channel === '' ? ' on' : ''}`} onClick={() => setChannel('')}>
              Tüm pazaryerleri
            </button>
            {seen.map((c) => (
              <button key={c} className={`chip select${channel === c ? ' on' : ''}`} onClick={() => setChannel(c)}>
                {CHANNEL_NAMES[c] ?? c}
              </button>
            ))}
          </>
        )}
      </div>

      {report.error && <ErrorBanner message={report.error} onRetry={() => void report.reload()} />}
      {!r ? (
        <Spinner />
      ) : (
        <>
          <Banners r={r} canEdit={canEdit} asking={asking} onSync={() => void askSync()} onCosts={toCosts} />
          <Tiles r={r} />
          <Waterfall r={r} />
          <div className="tabs" role="tablist">
            {(
              [
                ['orders', `Siparişler (${fmt.n(r.counts.orders)})`],
                ['products', `Ürünler (${fmt.n(r.products.length)})`],
                ['deductions', 'Kesintiler'],
                ['costs', 'Maliyetler'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                ref={key === 'costs' ? costsTab : undefined}
                role="tab"
                aria-selected={tab === key}
                className={tab === key ? 'on' : ''}
                onClick={() => setTab(key)}
              >
                {label}
              </button>
            ))}
          </div>
          {tab === 'orders' && <Orders r={r} />}
          {tab === 'products' && <Products rows={r.products} />}
          {tab === 'deductions' && <Deductions r={r} />}
          {tab === 'costs' && <Costs sold={r.products} canEdit={canEdit} onSaved={() => setRevision((n) => n + 1)} />}
          <HowItWorks />
        </>
      )}
    </>
  );
}

/** What the figures still lack: the marketplaces' records (and their permission), the estimates, the costs not entered. */
function Banners(props: { r: ProfitReport; canEdit: boolean; asking: boolean; onSync: () => void; onCosts: () => void }) {
  const { r } = props;
  const lines = r.sync.map((s) => ({ s, ...syncText(s, fmt.stamp) }));
  // a missing finance role (or a read that failed) in red, each with what to do; the other accounts in one line each
  const broken = lines.filter((l) => l.tone === 'bad');
  const fine = lines.filter((l) => l.tone !== 'bad');
  const pending = pendingText(r.counts);
  const cargoUnknown = cargoUnknownText(r.counts);
  const button = props.canEdit && r.sync.some((s) => s.supported) && (
    <button className="btn sm" disabled={props.asking} onClick={props.onSync} title="Pazaryerlerinin finans kayıtlarını şimdi okur (hesap başına 15 dakikada bir)">
      <Icon name="refresh" size={16} /> Şimdi güncelle
    </button>
  );
  return (
    <>
      {broken.map((l) => (
        <div key={l.s.credentialId} className="banner bad" role="alert">
          <Icon name="alert" />
          <div style={{ flex: 1 }}>
            {l.text}
            {l.s.errorAt && <div style={{ fontWeight: 500, fontSize: 13 }}>Son deneme: {fmt.stamp(l.s.errorAt)}</div>}
          </div>
          {button}
        </div>
      ))}
      {fine.length > 0 && (
        <div className="banner info-soft profit-sync">
          <Icon name="refresh" />
          <div style={{ flex: 1 }} className="profit-sync-lines">
            {fine.map((l) => (
              <span key={l.s.credentialId}>{l.text}</span>
            ))}
          </div>
          {broken.length === 0 && button}
        </div>
      )}
      {r.sync.length === 0 && r.counts.orders > 0 && (
        <div className="banner info-soft">
          <Icon name="info" />
          <div style={{ flex: 1 }}>Bağlı pazaryeri hesabınız yok: pazaryeri kesintileri hesaba katılmadı.</div>
        </div>
      )}
      {pending && (
        <div className="banner warn">
          <Icon name="clock" />
          <div style={{ flex: 1 }}>{pending}</div>
        </div>
      )}
      {cargoUnknown && (
        <div className="banner info-soft">
          <Icon name="truck" />
          <div style={{ flex: 1 }}>{cargoUnknown}</div>
        </div>
      )}
      {r.counts.noFinance > 0 && (
        <div className="banner info-soft">
          <Icon name="info" />
          <div style={{ flex: 1 }}>
            {fmt.n(r.counts.noFinance)} sipariş finans kayıtları okunamayan pazaryerlerinden (
            {r.channels
              .filter((c) => !c.supported)
              .map((c) => CHANNEL_NAMES[c.channel] ?? c.channel)
              .join(', ')}
            ): bu siparişlerin pazaryeri kesintileri bilinmiyor, hesaba katılmadı.
          </div>
        </div>
      )}
      {r.counts.productsWithoutCost > 0 && (
        <div className="banner warn">
          <Icon name="tag" />
          <div style={{ flex: 1 }}>
            {fmt.n(r.counts.productsWithoutCost)} ürünün maliyeti girilmedi ({fmt.n(r.counts.unitsWithoutCost)} adet): net kâra katılmadı.
          </div>
          <button className="btn sm" onClick={props.onCosts}>
            Maliyetleri gir
          </button>
        </div>
      )}
      {r.counts.otherCurrency > 0 && (
        <div className="banner info-soft">
          <Icon name="info" />
          <div style={{ flex: 1 }}>{fmt.n(r.counts.otherCurrency)} sipariş TL dışında bir para biriminde: bu sayfada sayılmadı.</div>
        </div>
      )}
    </>
  );
}

function Tile(props: { value: string; label: string; hint: ReactNode; loss?: boolean; strong?: boolean }) {
  return (
    <div className={`card kpi${props.loss ? ' loss' : ''}${props.strong ? ' profit-main' : ''}`}>
      <span className={`n${props.value.length > 11 ? ' long' : ''}`}>{props.value}</span>
      <span className="l">{props.label}</span>
      <span className="h">{props.hint}</span>
    </div>
  );
}

function Tiles({ r }: { r: ProfitReport }) {
  const t = r.totals;
  return (
    <div className="grid act-tiles profit-tiles">
      <Tile value={fmt.money(t.ciro)} label="Ciro (KDV dahil)" hint={`KDV hariç ${fmt.money(t.ciroNet)} · ${fmt.n(r.counts.orders)} sipariş`} />
      <Tile value={fmt.money(t.marketplace)} label="Pazaryeri kesintileri" hint="Komisyon, kargo, hizmet bedelleri, iade ve indirimler, ceza ve reklam (KDV hariç)" />
      <Tile value={fmt.money(t.pronto)} label="Pronto Depo hizmetleri" hint={r.periodFeesApplied ? 'Sipariş ücretleri, depolama ve mal kabul (KDV hariç)' : 'Siparişlerin ücretleri (KDV hariç); depolama tüm pazaryerlerinde sayılır'} />
      <Tile value={fmt.money(t.productCost)} label="Ürün maliyeti" hint={r.counts.unitsWithoutCost > 0 ? `${fmt.n(r.counts.unitsWithoutCost)} adedin maliyeti girilmedi` : 'Girdiğiniz birim maliyetlerden (KDV hariç)'} />
      <Tile value={fmt.money(t.profit)} label="Net kâr (KDV hariç)" hint="Ciro − kesintiler − Pronto Depo − maliyet" loss={below(t.profit)} strong />
      <Tile value={marginText(t.margin)} label="Kâr marjı" hint="Net kâr ÷ ciro (KDV hariç)" loss={t.margin !== null && t.margin < 0} />
      <Tile
        value={fmt.money(t.cash)}
        label="Pazaryerinden hak ediş (KDV dahil)"
        hint={`Ciro ve pazaryeri kesintileri faturalandığı gibi${below(t.withholding) ? ` · stopaj ${fmt.money(t.withholding)} ayrıca kesilir` : ''}`}
      />
    </div>
  );
}

/** From the ciro to the net profit, a bar a step, all on one scale (CSS only: the CSP loads no chart library). */
function Waterfall({ r }: { r: ProfitReport }) {
  const w = waterfall(r.totals);
  if (r.counts.orders === 0 && r.totals.profit === '0.00') return null;
  return (
    <section className="card">
      <div className="card-h">
        <h2>Cirodan net kâra</h2>
        <span className="sub">KDV hariç</span>
      </div>
      <div className="card-b wf">
        {w.steps.map((s) => {
          const box = barBox(s, w.min, w.max);
          const tone = s.total ? (s.key === 'profit' ? (s.amount < 0 ? 'loss' : 'profit') : 'total') : s.amount < 0 ? 'down' : 'up';
          return (
            <div key={s.key} className={`wf-row${s.total ? ' total' : ''}`}>
              <span className="wf-label">{s.label}</span>
              <span className="wf-track" aria-hidden>
                {w.min < 0 && <i className="wf-zero" style={{ left: `${barBox({ from: 0, to: 0 }, w.min, w.max).left}%` }} />}
                <i className={`wf-bar ${tone}`} style={{ left: `${box.left}%`, width: `${box.width}%` }} title={fmt.money(s.amount)} />
              </span>
              <span className={`wf-amount num${s.amount < 0 ? ' profit-neg' : ''}`}>{fmt.money(s.amount)}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function StateChip({ o }: { o: Pick<ProfitOrderRow, 'state' | 'estimated'> }) {
  return (
    <span className={`chip ${STATE_TONE[o.state]}`} title={estimatedText(o.estimated) ?? 'Pazaryerinin kayıtlarından'}>
      {stateLabel(o.state)}
    </span>
  );
}

function Orders({ r }: { r: ProfitReport }) {
  const [open, setOpen] = useState<string | null>(null);
  const sort = useSort<ProfitOrderRow>('profit-orders', {
    date: { label: 'Tarih', value: (o) => o.orderedAt, desc: true },
    number: { label: 'Sipariş', value: (o) => o.orderNumber },
    state: { label: 'Durum', value: (o) => ['final', 'partial', 'estimate'].indexOf(o.state) },
    ciro: { label: TABLE_CIRO.label, value: (o) => Number(TABLE_CIRO.of(o)), desc: true },
    marketplace: { label: 'Kesintiler', value: (o) => Number(o.marketplace) },
    pronto: { label: 'Pronto Depo', value: (o) => Number(o.pronto) },
    cost: { label: 'Maliyet', value: (o) => Number(o.productCost) },
    profit: { label: 'Net kâr', value: (o) => Number(o.profit), desc: true },
    margin: { label: 'Marj', value: (o) => o.margin, desc: true },
  });
  if (r.orders.length === 0) {
    return (
      <section className="card">
        <Empty title="Bu dönemde kargoya verilen sipariş yok">
          <p>Siparişler sipariş tarihine göre sayılır. Daha uzun bir dönem seçin: “Son 3 ay” ya da “Bu yıl”.</p>
        </Empty>
      </section>
    );
  }
  return (
    <section className="card">
      <div className="card-h">
        <h2>Sipariş sipariş</h2>
        <span className="sub">Sipariş tarihine göre (Türkiye saati); satıra dokunun, kayıtları açılır</span>
        <span className="spacer" />
        {sort.picker}
        <button className="btn sm" onClick={() => downloadCsv(`karlilik-siparisler-${r.from}-${r.to}.csv`, ordersCsv(r.orders, (iso) => fmt.date(iso)))}>
          <Icon name="download" size={16} /> CSV
        </button>
      </div>
      {r.counts.orders > r.orders.length && (
        <p className="hint" style={{ padding: '8px 18px 0' }}>
          Liste en yeni {fmt.n(PROFIT_ORDER_LIMIT)} siparişle sınırlı; toplamlar dönemin {fmt.n(r.counts.orders)} siparişinin hepsini sayar.
        </p>
      )}
      <div className="table-wrap">
        <table className="t stack-sm profit-table">
          <thead>
            <tr>
              {sort.th('number')}
              {sort.th('date', 'hide-sm')}
              {sort.th('state')}
              {sort.th('ciro', 'num')}
              {sort.th('marketplace', 'num')}
              {sort.th('pronto', 'num hide-sm')}
              {sort.th('cost', 'num hide-sm')}
              {sort.th('profit', 'num')}
              {sort.th('margin', 'num hide-sm')}
            </tr>
          </thead>
          <tbody>
            {sort.apply(r.orders).map((o) => {
              const expanded = open === o.orderId;
              return (
                <Fragment key={o.orderId}>
                  <tr className="clickable" onClick={() => setOpen(expanded ? null : o.orderId)} aria-expanded={expanded}>
                    <td>
                      <b className="mono">{o.orderNumber}</b>
                      <div className="muted" style={{ fontSize: 12.5 }}>
                        {CHANNEL_NAMES[o.channel] ?? o.channel}
                        {o.account ? ` · ${o.account}` : ''}
                      </div>
                    </td>
                    <td className="nowrap hide-sm">{fmt.date(o.orderedAt)}</td>
                    <td>
                      <StateChip o={o} />
                    </td>
                    <td className="num" data-label={TABLE_CIRO.label}>
                      {fmt.money(TABLE_CIRO.of(o))}
                    </td>
                    <td className="num" data-label="Kesinti">
                      <Amount value={o.marketplace} />
                    </td>
                    <td className="num hide-sm">
                      <Amount value={o.pronto} />
                    </td>
                    <td className="num hide-sm">{o.unitsWithoutCost > 0 && o.productCost === '0.00' ? <span className="chip warn">Girilmedi</span> : <Amount value={o.productCost} />}</td>
                    <td className="num" data-label="Net kâr">
                      <Amount value={o.profit} strong />
                    </td>
                    <td className="num hide-sm">{marginText(o.margin)}</td>
                  </tr>
                  {expanded && (
                    <tr className="sub-row">
                      <td colSpan={9}>
                        <OrderDetail o={o} supported={r.channels.find((c) => c.channel === o.channel)?.supported ?? false} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * One order opened: its figures part by part, the marketplace's records and the warehouse's charges behind them.
 * `supported`: its marketplace's finance records are read (else none will ever come).
 */
function OrderDetail({ o, supported }: { o: ProfitOrderRow; supported: boolean }) {
  const detail = useLoad(() => api<ProfitOrderDetail>('GET', `/api/profit/orders/${o.orderId}`), [o.orderId]);
  const parts: [string, string][] = [
    ['Ciro (KDV hariç)', o.ciroNet],
    ['Komisyon', o.commission],
    ['Kargo', o.cargo],
    ['Platform ve hizmet bedelleri', o.fees],
    ['İadeler ve indirimler', o.returns],
    ['Ceza, reklam ve diğer', o.other],
    ['Pronto Depo hizmetleri', o.pronto],
    ['Ürün maliyeti', o.productCost],
  ];
  const estimated = estimatedText(o.estimated);
  return (
    <div className="stack profit-detail" style={{ gap: 10 }}>
      <div className="row" style={{ gap: '4px 16px' }}>
        {parts
          .filter(([, v], i) => i === 0 || v !== '0.00')
          .map(([label, v]) => (
            <span key={label} className="nowrap">
              <span className="muted">{label}:</span> <Amount value={v} />
            </span>
          ))}
        <span className="nowrap">
          <span className="muted">Hak ediş (KDV dahil):</span> <Amount value={o.cash} />
        </span>
        {o.withholding !== '0.00' && (
          <span className="nowrap">
            <span className="muted">Stopaj:</span> {fmt.money(o.withholding)}
          </span>
        )}
      </div>
      <div className="muted" style={{ fontSize: 13 }}>
        Kargo: {CARGO_SOURCE_TEXT[o.cargoSource]}
        {estimated && ` · ${estimated}${supported && o.channel !== 'manual' ? ' (pazaryerinin kaydı gelince kesinleşir)' : ''}`}
        {o.unitsWithoutCost > 0 && ` · ${fmt.n(o.unitsWithoutCost)} adedin maliyeti girilmedi`}
      </div>
      {detail.error && <ErrorBanner message={detail.error} onRetry={() => void detail.reload()} />}
      {!detail.data ? (
        <Spinner />
      ) : (
        <>
          {detail.data.rows.length > 0 ? (
            <div className="table-wrap">
              <table className="t">
                <thead>
                  <tr>
                    <th>Pazaryeri kaydı</th>
                    <th className="hide-sm">Tarih</th>
                    <th className="hide-sm">Barkod</th>
                    <th className="num">Tutar</th>
                    <th className="num">KDV hariç</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data.rows.map((f) => (
                    <tr key={f.id}>
                      <td>{kindText(f.kind, f.label)}</td>
                      <td className="nowrap hide-sm">{fmt.date(f.occurredAt)}</td>
                      <td className="mono hide-sm">{f.barcode ?? ''}</td>
                      <td className="num">
                        <Amount value={f.amount} />
                        {!f.vatIncluded && f.kind !== 'withholding' && f.kind !== 'payout' && f.kind !== 'excluded' && <span className="muted"> +KDV</span>}
                      </td>
                      <td className="num">
                        <Amount value={f.net} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <span className="muted">{noRecordsText(o.channel, supported)}</span>
          )}
          {detail.data.charges.length > 0 && (
            <div className="muted" style={{ fontSize: 13 }}>
              Pronto Depo: {detail.data.charges.map((c) => `${CHARGE_TYPE_LABELS[c.type]} ${fmt.money(c.amount)} (${fmt.date(c.serviceDate)})`).join(' · ')}
            </div>
          )}
        </>
      )}
      <div>
        <a className="btn sm ghost" href={`#/siparisler?q=${encodeURIComponent(o.orderNumber)}`}>
          <Icon name="list" size={16} /> Siparişi aç
        </a>
      </div>
    </div>
  );
}

function Products({ rows }: { rows: readonly ProfitProductRow[] }) {
  const sort = useSort<ProfitProductRow>('profit-products', {
    sku: { label: 'Ürün', value: (p) => p.sku },
    units: { label: 'Adet', value: (p) => p.units, desc: true },
    ciro: { label: TABLE_CIRO.label, value: (p) => Number(TABLE_CIRO.of(p)), desc: true },
    marketplace: { label: 'Kesintiler', value: (p) => Number(p.marketplace) },
    pronto: { label: 'Pronto Depo', value: (p) => Number(p.pronto) },
    cost: { label: 'Maliyet', value: (p) => Number(p.productCost) },
    profit: { label: 'Net kâr', value: (p) => Number(p.profit), desc: true },
    margin: { label: 'Marj', value: (p) => p.margin, desc: true },
  });
  if (rows.length === 0) {
    return (
      <section className="card">
        <Empty title="Bu dönemde satılan ürün yok" />
      </section>
    );
  }
  return (
    <section className="card">
      <div className="card-h">
        <h2>Ürün ürün</h2>
        <span className="sub">Siparişin kesintileri satırlara fiyatlarına göre paylaştırılır; ürüne ait kayıtlar doğrudan ürüne yazılır</span>
        <span className="spacer" />
        {sort.picker}
      </div>
      <div className="table-wrap">
        <table className="t stack-sm profit-table">
          <thead>
            <tr>
              {sort.th('sku')}
              {sort.th('units', 'num')}
              {sort.th('ciro', 'num')}
              {sort.th('marketplace', 'num')}
              {sort.th('pronto', 'num hide-sm')}
              {sort.th('cost', 'num hide-sm')}
              {sort.th('profit', 'num')}
              {sort.th('margin', 'num hide-sm')}
            </tr>
          </thead>
          <tbody>
            {sort.apply(rows).map((p) => (
              <tr key={p.productId ?? 'none'}>
                <td>
                  <b className="mono">{p.sku}</b> {p.name}
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {fmt.n(p.orders)} sipariş
                    {p.returnedUnits > 0 && ` · ${fmt.n(p.returnedUnits)} adet iade`}
                    {p.unitCost !== null ? ` · birim maliyet ${fmt.money(p.unitCost)}` : p.productId ? ' · maliyet girilmedi' : ''}
                  </div>
                </td>
                <td className="num" data-label="Adet">
                  {fmt.n(p.units)}
                </td>
                <td className="num" data-label={TABLE_CIRO.label}>
                  {fmt.money(TABLE_CIRO.of(p))}
                </td>
                <td className="num" data-label="Kesinti">
                  <Amount value={p.marketplace} />
                </td>
                <td className="num hide-sm">
                  <Amount value={p.pronto} />
                </td>
                <td className="num hide-sm">{p.unitsWithoutCost > 0 && p.productCost === '0.00' ? <span className="chip warn">Girilmedi</span> : <Amount value={p.productCost} />}</td>
                <td className="num" data-label="Net kâr">
                  <Amount value={p.profit} strong />
                </td>
                <td className="num hide-sm">{marginText(p.margin)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AccountRows({ rows }: { rows: readonly ProfitAccountRow[] }) {
  return (
    <div className="table-wrap">
      <table className="t">
        <thead>
          <tr>
            <th>Kayıt</th>
            <th className="num hide-sm">Adet</th>
            <th className="num">KDV hariç</th>
            <th className="num hide-sm">Faturalandığı gibi</th>
            <th>Kâra</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => {
            // a credit the marketplace paid (a compensation) is added to the profit, not taken off it
            const chip = accountChip(a);
            return (
              <tr key={`${a.kind}\n${a.label}`} className={a.counted ? undefined : 'muted-row'}>
                <td>{kindText(a.kind, a.label)}</td>
                <td className="num hide-sm">{fmt.n(a.count)}</td>
                <td className="num">
                  <Amount value={a.net} />
                </td>
                <td className="num hide-sm">{fmt.money(a.cash)}</td>
                <td>
                  <span className={chip.tone ? `chip ${chip.tone}` : 'chip'}>{chip.text}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** What belongs to no order of the period: the account's own records, the warehouse's fees of no order, and orders the warehouse never held. */
function Deductions({ r }: { r: ProfitReport }) {
  const fees = r.periodFees.reduce((n, f) => n + Number(f.amount), 0);
  return (
    <>
      <section className="card">
        <div className="card-h">
          <h2>Hesabın kendi kayıtları</h2>
          <span className="sub">Hiçbir siparişe bağlanmayan pazaryeri kayıtları: reklam, platform bedeli faturaları, cezalar, düzeltmeler</span>
        </div>
        {r.account.length === 0 ? (
          <div className="card-b muted">Bu dönemde siparişe bağlanmayan pazaryeri kaydı yok.</div>
        ) : (
          <AccountRows rows={r.account} />
        )}
        <div className="card-b muted" style={{ fontSize: 13 }}>
          Düşülenler net kârdan çıkar, kâra eklenenler (ör. pazaryerinin ödediği tazminat) eklenir: toplam {fmt.money(r.totals.accountNet)}. Stopaj vergiden mahsup
          edilir, maliyet değildir; pazaryerinin ödemeleri, komisyon ve iade faturaları siparişlerdeki kayıtları tekrarlar: kâra katılmaz.
          {r.payouts !== '0.00' && ` Bu dönemde pazaryerinden hesabınıza ödenen: ${fmt.money(r.payouts)}.`}
        </div>
      </section>

      <section className="card">
        <div className="card-h">
          <h2>Pronto Depo’nun dönem ücretleri</h2>
          <span className="sub">Siparişe bağlı olmayan ücretler, hizmet tarihine göre (KDV hariç)</span>
        </div>
        {r.periodFees.length === 0 ? (
          <div className="card-b muted">Bu dönemde depolama ya da mal kabul ücreti yok.</div>
        ) : (
          <div className="table-wrap">
            <table className="t totals">
              <thead>
                <tr>
                  <th>Hizmet</th>
                  <th className="num">Kalem</th>
                  <th className="num">Tutar</th>
                </tr>
              </thead>
              <tbody>
                {r.periodFees.map((f) => (
                  <tr key={f.type}>
                    <td>{CHARGE_TYPE_LABELS[f.type]}</td>
                    <td className="num">{fmt.n(f.count)}</td>
                    <td className="num">{fmt.money(f.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>Toplam</td>
                  <td className="num">{fmt.money(fees)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        {!r.periodFeesApplied && r.periodFees.length > 0 && (
          <div className="card-b muted" style={{ fontSize: 13 }}>
            Tek bir pazaryerine bakılırken bu ücretler net kârdan düşülmez: depolama bütün pazaryerlerinin ortak gideridir.
          </div>
        )}
      </section>

      <section className="card">
        <div className="card-h">
          <h2>Depo dışı siparişler</h2>
          <span className="sub">Depoya hiç gelmemiş siparişlerin pazaryeri kayıtları (ör. depodan önceki siparişler): toplamlara katılmaz</span>
        </div>
        {r.outside.rows.length === 0 ? (
          <div className="card-b muted">Yok.</div>
        ) : (
          <>
            <div className="card-b row" style={{ gap: '4px 16px' }}>
              <span>
                <span className="muted">Sipariş:</span> {fmt.n(r.outside.orders)}
              </span>
              <span>
                <span className="muted">KDV hariç:</span> <Amount value={r.outside.net} />
              </span>
              <span>
                <span className="muted">Faturalandığı gibi:</span> {fmt.money(r.outside.cash)}
              </span>
            </div>
            <AccountRows rows={r.outside.rows} />
          </>
        )}
      </section>
    </>
  );
}

/** Rows of the cost table drawn at once: a large catalogue is found by its search. */
const COST_ROWS = 200;

/** The seller's unit costs: the products sold in the period first; saved on Enter or on leaving the field. */
function Costs(props: { sold: readonly ProfitProductRow[]; canEdit: boolean; onSaved: () => void }) {
  const costs = useLoad(() => api<ProductCostView[]>('GET', '/api/profit/costs'), []);
  const [search, setSearch] = useState('');
  const [saved, setSaved] = useState<Readonly<Record<string, ProductCostView>>>({});
  const all = costRows((costs.data ?? []).map((c) => saved[c.productId] ?? c), props.sold, search);
  const list = all.slice(0, COST_ROWS);
  return (
    <section className="card">
      <div className="card-h">
        <h2>Ürün maliyetleri</h2>
        <span className="sub">Birim maliyet KDV hariç, bir depo birimi için. Yalnız siz görürsünüz; depo görmez.</span>
        <span className="spacer" />
        <input className="input" style={{ maxWidth: 240 }} placeholder="SKU ya da ürün adı" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Ürün ara" />
      </div>
      {costs.error && <ErrorBanner message={costs.error} onRetry={() => void costs.reload()} />}
      {!costs.data ? (
        <Spinner />
      ) : list.length === 0 ? (
        <Empty title={search ? 'Aramaya uyan ürün yok' : 'Ürününüz yok'} />
      ) : (
        <div className="table-wrap">
          <table className="t stack-sm">
            <thead>
              <tr>
                <th>Ürün</th>
                <th className="num">Dönemde satılan</th>
                <th>Birim maliyet (KDV hariç)</th>
                <th className="hide-sm">Son değişiklik</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.productId}>
                  <td>
                    <b className="mono">{c.sku}</b> {c.name}
                  </td>
                  <td className="num" data-label="Satılan">
                    {c.sold > 0 ? fmt.n(c.sold) : '—'}
                  </td>
                  <td>
                    <CostField
                      cost={c}
                      canEdit={props.canEdit}
                      onSaved={(next) => {
                        setSaved((before) => ({ ...before, [next.productId]: next }));
                        props.onSaved();
                      }}
                    />
                  </td>
                  <td className="nowrap hide-sm muted">{c.changedAt ? fmt.stamp(c.changedAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {all.length > list.length && (
        <p className="hint" style={{ padding: '8px 18px 0' }}>
          {fmt.n(all.length)} üründen ilk {fmt.n(list.length)} tanesi gösteriliyor: diğerlerini SKU ya da ürün adıyla arayın.
        </p>
      )}
      <div className="card-b muted" style={{ fontSize: 13 }}>
        İlk girdiğiniz maliyet önceki siparişlere de uygulanır; sonraki değişiklikler yalnız o andan sonraki siparişleri etkiler, geçmişi değiştirmez. Kutuyu
        boşaltmak maliyeti siler (o andan sonra maliyet girilmemiş sayılır).
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="card">
      <div className="card-h">
        <Icon name="info" />
        <h2>Net kâr nasıl hesaplanır?</h2>
      </div>
      <div className="card-b">
        <ul className="profit-how">
          <li>
            Ciro kutusu ve “Pazaryerinden hak ediş” dışında bütün tutarlar <b>KDV hariç</b>tir (tablolardaki ciro da): ciro her satırın KDV oranıyla, pazaryeri
            kesintileri %20 KDV ile ayrılır (pazaryeri KDV’yi kendisi bildiriyorsa o tutarla). Pronto Depo ücretleri ve girdiğiniz maliyetler zaten KDV hariçtir; KDV
            faturalarda eklenir.
          </li>
          <li>Net kâr = ciro − pazaryeri kesintileri (komisyon, kargo, platform ve hizmet bedelleri, iade ve indirimler, ceza ve reklam) − Pronto Depo hizmetleri − ürün maliyeti.</li>
          <li>
            <b>Stopaj</b> (%1 e-ticaret stopajı) maliyet değildir, vergiden mahsup edilir: ayrıca gösterilir. Pazaryerinin ödemeleri, komisyon faturaları ve iade
            faturaları siparişlerdeki kayıtları tekrarladığı için kâra katılmaz.
          </li>
          <li>
            Pazaryerinin kayıtları sipariş <b>teslim edildikten sonra</b> gelir. O zamana kadar ciro siparişteki fiyatlardan, komisyon siparişteki orandan, kargo son 90
            günün benzer gönderilerinden hesaplanır (<b>Tahmini</b>). Kargo faturası haftalar sonra gelir; o zamana kadar sipariş <b>Kısmi</b> görünür.
          </li>
          <li>
            “Pazaryerinden hak ediş” pazaryerinin size ödeyeceği tutardır: ciro ve kesintiler faturalandığı gibi (KDV dahil). Siparişler sipariş tarihine göre (Türkiye
            saati) sayılır.
          </li>
        </ul>
      </div>
    </section>
  );
}
